import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import html2canvas from 'html2canvas';
import { X as IconX, Loader, Filter, FilterX, Palette, Layers, Camera } from 'lucide-react';

import { PROJECT_STATUSES } from '../../constants/appConstants';
import { fetchOzmapDataForProjects } from '../../utils/projectMapUtils';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';

// --- FUNÇÃO PARA OBTER OPÇÕES DE ESTILO DO MARCADOR (PROJETOS) ---
const getPathOptions = (status) => {
    const statusInfo = PROJECT_STATUSES.find(s => s.value.toLowerCase() === (status || '').toLowerCase());
    const color = statusInfo ? statusInfo.color : '#6b7280'; // Cor padrão cinza

    return {
        fillColor: color,
        color: color,
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8,
        radius: 2.5,
    };
};

// --- FUNÇÕES DE RENDERIZAÇÃO GEOJSON (EXTRAÍDAS PARA ESTABILIDADE) ---

const pointToLayer = (feature, latlng) => {
    const options = getPathOptions(feature.properties.projectStatus);
    return L.circleMarker(latlng, options);
};

const ctoPointToLayer = (feature, latlng) => {
    return L.circleMarker(latlng, {
        fillColor: '#FFFF00', color: '#FFFF00', weight: 2, opacity: 1, fillOpacity: 0.9, radius: 7
    });
};

const ceoPointToLayer = (feature, latlng) => {
     return L.circleMarker(latlng, {
        fillColor: '#00FFFF', color: '#00FFFF', weight: 2, opacity: 1, fillOpacity: 0.9, radius: 7
    });
};

const onEachFeature = (feature, layer) => {
    if (feature.properties) {
        const { projectName, endereco, projectStatus, name } = feature.properties;
        if (projectName) { // Postes do projeto
             const popupContent = `
                <div>
                    <b>Projeto:</b> ${projectName}<br />
                    <b>Endereço:</b> ${endereco}<br />
                    <b>Status:</b> ${projectStatus}
                </div>
            `;
            layer.bindPopup(popupContent);
        } else if (name) { // Caixas Ozmap
            layer.bindPopup(`<b>${feature.properties.type}:</b> ${name}`);
        }
    }
};

// --- COMPONENTE DA LEGENDA DO MAPA ---
const MapEvents = ({ onContextMenu }) => {
    useMapEvents({
        contextmenu(e) {
            onContextMenu(e);
        },
    });
    return null;
};

const MapLegend = () => (
    <div className="absolute bottom-4 left-4 bg-white/80 dark:bg-slate-800/80 p-3 rounded-lg shadow-lg z-[1000]">
        <h4 className="font-bold text-sm mb-2 text-slate-700 dark:text-slate-200">Legenda de Status</h4>
        <div className="space-y-1">
            {PROJECT_STATUSES.map(status => (
                <div key={status.value} className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: status.color }}></span>
                    <span className="text-xs text-slate-600 dark:text-slate-300">{status.label}</span>
                </div>
            ))}
        </div>
    </div>
);

// --- COMPONENTE PARA AJUSTAR O TAMANHO DO MAPA ---
const MapResizer = () => {
    const map = useMap();
    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
        }, 100);

        return () => clearTimeout(timer);
    }, [map]);

    return null;
};

// --- COMPONENTE DO FILTRO DE STATUS ---
const StatusFilter = ({ selectedStatuses, onStatusChange }) => {
    const [isFilterVisible, setIsFilterVisible] = useState(false);

    const handleCheckboxChange = (statusValue, isChecked) => {
        const newSelectedStatuses = isChecked
            ? [...selectedStatuses, statusValue]
            : selectedStatuses.filter(s => s !== statusValue);
        onStatusChange(newSelectedStatuses);
    };

    if (!isFilterVisible) {
        return (
            <div className="absolute top-4 right-4 bg-white/80 dark:bg-slate-800/80 p-3 rounded-full shadow-lg z-[1000]">
                <button
                    onClick={() => setIsFilterVisible(true)}
                    className="flex items-center justify-center text-slate-600 dark:text-slate-200 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                    aria-label="Abrir filtro de status"
                >
                    <Filter className="h-8 w-8" />
                </button>
            </div>
        );
    }

    return (
        <div className="absolute top-4 right-4 bg-white/80 dark:bg-slate-800/80 p-3 rounded-lg shadow-lg z-[1000]">
            <div className="flex justify-between items-center mb-2">
                <h4 className="font-bold text-sm text-slate-700 dark:text-slate-200">Filtrar Status</h4>
                <button
                    onClick={() => setIsFilterVisible(false)}
                    className="text-slate-500 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    aria-label="Fechar filtro de status"
                >
                    <FilterX className="h-5 w-5" />
                </button>
            </div>
            <div className="space-y-1">
                {PROJECT_STATUSES.map(status => (
                    <label key={status.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:focus:ring-blue-600 dark:focus:ring-offset-slate-800"
                            checked={selectedStatuses.includes(status.value)}
                            onChange={(e) => handleCheckboxChange(status.value, e.target.checked)}
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-300">{status.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );
};

// --- COMPONENTE PARA CONTROLE DE CAMADAS (LAYERS) ---
const LayersControl = ({ layerVisibility, onToggleLayer, isLoading }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!isOpen) {
        return (
            <div className="absolute top-4 right-20 bg-white/80 dark:bg-slate-800/80 p-3 rounded-full shadow-lg z-[1000]">
                <button
                    onClick={() => setIsOpen(true)}
                    className="flex items-center justify-center text-slate-600 dark:text-slate-200 hover:text-blue-500 dark:hover:text-blue-400 transition-colors relative"
                    aria-label="Camadas"
                    title="Camadas (CTO, CEO)"
                >
                    {isLoading ? <Loader className="h-5 w-5 animate-spin text-blue-500" /> : <Layers className="h-5 w-5" />}
                </button>
            </div>
        );
    }

    return (
        <div className="absolute top-4 right-20 bg-white/80 dark:bg-slate-800/80 p-3 rounded-lg shadow-lg z-[1000] min-w-[150px]">
             <div className="flex justify-between items-center mb-2">
                <h4 className="font-bold text-sm text-slate-700 dark:text-slate-200">Camadas</h4>
                <button
                    onClick={() => setIsOpen(false)}
                    className="text-slate-500 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    aria-label="Fechar camadas"
                >
                    <IconX className="h-5 w-5" />
                </button>
            </div>
            {isLoading ? (
                <div className="text-xs text-slate-500 flex items-center gap-2 py-2">
                    <Loader className="h-3 w-3 animate-spin" /> Carregando dados...
                </div>
            ) : (
                <div className="space-y-2">
                     <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 p-1 rounded">
                        <input
                            type="checkbox"
                            className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                            checked={layerVisibility.ctos}
                            onChange={(e) => onToggleLayer('ctos', e.target.checked)}
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-1 font-medium">
                            <span className="w-2 h-2 rounded-full bg-[#FFFF00] border border-black inline-block"></span>
                            CTOs
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 p-1 rounded">
                        <input
                            type="checkbox"
                            className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                            checked={layerVisibility.ceos}
                            onChange={(e) => onToggleLayer('ceos', e.target.checked)}
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-1 font-medium">
                             <span className="w-2 h-2 rounded-full bg-[#00FFFF] border border-black inline-block"></span>
                            CEOs
                        </span>
                    </label>
                </div>
            )}
        </div>
    );
};

// --- COMPONENTE PARA FOCO AUTOMÁTICO E ZOOM ---
const MapFocusController = ({ poles }) => {
    const map = useMap();

    useEffect(() => {
        if (poles && poles.length > 0) {
            const bounds = poles.map(p => [p.latitude, p.longitude]);
            map.fitBounds(bounds, { padding: [50, 50] });
        } else {
            // Fallback para o centro do Brasil se não houver postes
            map.setView([-14.235, -51.925], 5);
        }
    }, [poles, map]);

    return null;
};

// --- COMPONENTE PRINCIPAL DO MODAL ---
const ProjectsMapModal = ({ isOpen, onClose, projects, theme, darkMapEnabled }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [selectedStatuses, setSelectedStatuses] = useState(PROJECT_STATUSES.map(s => s.value));
    const [localMapTheme, setLocalMapTheme] = useState(null);
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });

    // Novos estados para Ozmap Layers
    const [ozmapData, setOzmapData] = useState({ ctos: [], ceos: [] });
    const [isOzmapLoading, setIsOzmapLoading] = useState(false);
    const [layerVisibility, setLayerVisibility] = useState({ ctos: false, ceos: false });
    const [feedback, setFeedback] = useState({ visible: false, message: '', x: 0, y: 0 });

    const mapContainerRef = useRef(null);

    // Processa os dados brutos de postes
    const filteredPoles = useMemo(() => {
        if (!isOpen) return [];
        // ⚡ Bolt: Convert selectedStatuses to a Set for O(1) lookup
        // and filter projects FIRST before iterating their poles.
        // This avoids mapping over poles of projects whose status is not selected.
        const statusSet = new Set(selectedStatuses);

        return projects
            .filter(project => statusSet.has(project.status))
            .flatMap(project =>
                (project.postes || [])
                .filter(pole => pole.latitude && pole.longitude)
                .map(pole => ({
                    ...pole,
                    projectName: project.protocolo,
                    projectStatus: project.status,
                    endereco: pole.endereco || 'N/A'
                }))
            );
    }, [isOpen, projects, selectedStatuses]);

    // GEOJSONS PARA RENDERIZAÇÃO OTIMIZADA

    const polesGeoJson = useMemo(() => {
        if (filteredPoles.length === 0) return null;
        return {
            type: "FeatureCollection",
            features: filteredPoles.map(pole => ({
                type: "Feature",
                properties: {
                    id: pole.id,
                    projectName: pole.projectName,
                    projectStatus: pole.projectStatus,
                    endereco: pole.endereco
                },
                geometry: {
                    type: "Point",
                    coordinates: [pole.longitude, pole.latitude]
                }
            }))
        };
    }, [filteredPoles]);

    const ctosGeoJson = useMemo(() => {
        if (!ozmapData.ctos || ozmapData.ctos.length === 0) return null;
        return {
            type: "FeatureCollection",
            features: ozmapData.ctos.map(box => {
                if (!box.lat || !box.lng) return null;
                return {
                    type: "Feature",
                    geometry: { type: "Point", coordinates: [box.lng, box.lat] },
                    properties: { name: box.name, type: 'CTO' }
                };
            }).filter(f => f !== null)
        };
    }, [ozmapData.ctos]);

    const ceosGeoJson = useMemo(() => {
        if (!ozmapData.ceos || ozmapData.ceos.length === 0) return null;
        return {
            type: "FeatureCollection",
            features: ozmapData.ceos.map(box => {
                if (!box.lat || !box.lng) return null;
                return {
                    type: "Feature",
                    geometry: { type: "Point", coordinates: [box.lng, box.lat] },
                    properties: { name: box.name, type: 'CEO' }
                };
            }).filter(f => f !== null)
        };
    }, [ozmapData.ceos]);


    // ESTILIZAÇÃO E RENDERIZAÇÃO
    // Funções pointToLayer, ctoPointToLayer, ceoPointToLayer, e onEachFeature movidas para fora do componente para evitar recriação a cada render.

    useEffect(() => {
        if (isOpen) {
            setIsLoading(true);
            setSelectedStatuses(PROJECT_STATUSES.map(s => s.value));
            setLocalMapTheme(null);

            // Initial state reset
            setLayerVisibility({ ctos: false, ceos: false });
            setOzmapData({ ctos: [], ceos: [] });

            const timer = setTimeout(() => setIsLoading(false), 300);

            if (projects && projects.length > 0) {
                 setIsOzmapLoading(true);
                 fetchOzmapDataForProjects(projects)
                    .then(data => {
                        if (data) setOzmapData(data);
                    })
                    .catch(err => console.error("Error loading Ozmap layers:", err))
                    .finally(() => {
                        setIsOzmapLoading(false);
                    });
            } else {
                setIsOzmapLoading(false);
            }

            return () => clearTimeout(timer);
        }
    }, [isOpen, projects]);
    
    if (!isOpen) return null;

    const isGlobalDark = theme === 'dark' && darkMapEnabled;
    const isMapDark = localMapTheme !== null ? localMapTheme === 'dark' : isGlobalDark;

    const handleContextMenu = (e) => {
        e.originalEvent.preventDefault();
        setContextMenu({
            visible: true,
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY
        });
    };

    const closeContextMenu = () => {
        setContextMenu({ ...contextMenu, visible: false });
    };

    const showFeedbackMessage = (message, x, y) => {
        setFeedback({ visible: true, message, x, y });
        setTimeout(() => setFeedback(prev => ({ ...prev, visible: false })), 2000);
    };

    const handleScreenshot = async () => {
        if (!mapContainerRef.current) return;

        // Esconde o menu para não sair no print
        setContextMenu(prev => ({ ...prev, visible: false }));

        try {
            // Pequeno delay para garantir atualização do DOM/mapa
            await new Promise(r => setTimeout(r, 100));
            const canvas = await html2canvas(mapContainerRef.current, {
                useCORS: true,
                allowTaint: true,
                logging: false
            });

            canvas.toBlob((blob) => {
                if (!blob) return;
                try {
                    const item = new ClipboardItem({ 'image/png': blob });
                    navigator.clipboard.write([item]).then(() => {
                        showFeedbackMessage('Copiado!', contextMenu.x, contextMenu.y);
                    });
                } catch (err) {
                    console.error('Falha ao copiar imagem:', err);
                    alert('Não foi possível copiar a imagem para a área de transferência. Verifique as permissões.');
                }
            });
        } catch (error) {
            console.error('Erro ao tirar screenshot:', error);
        }
    };

    const toggleMapMode = () => {
        setLocalMapTheme(prev => {
            if (prev === null) return isGlobalDark ? 'light' : 'dark';
            return prev === 'dark' ? 'light' : 'dark';
        });
        closeContextMenu();
    };

    const handleToggleLayer = (layerName, isChecked) => {
        setLayerVisibility(prev => ({ ...prev, [layerName]: isChecked }));
    };

    const tileUrl = getMapTileUrl(isMapDark);
    const tileAttribution = getMapTileAttribution(isMapDark);

    const center = [-14.235, -51.925]; // Centro do Brasil
    const zoom = 5;

    return ReactDOM.createPortal(
        <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1050] flex justify-center items-center p-4 transition-opacity duration-300"
            onClick={onClose}
        >
            {/* Menu de Contexto */}
            {contextMenu.visible && (
                <div
                    className="fixed z-[1060] bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 w-56 overflow-hidden"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button onClick={toggleMapMode} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <Palette className="h-4 w-4 text-green-500" />
                        Alterar modo de cor
                    </button>
                    <button onClick={handleScreenshot} className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <Camera className="h-4 w-4 text-purple-500" />
                        Obter screenshot
                    </button>
                </div>
            )}

            {/* Feedback Toast */}
            {feedback.visible && (
                <div
                    className="fixed z-[1070] bg-black/80 text-white px-3 py-1 rounded-md text-sm pointer-events-none transition-opacity duration-300"
                    style={{ top: feedback.y - 40, left: feedback.x }}
                >
                    {feedback.message}
                </div>
            )}

            {/* Overlay para fechar menu */}
            {contextMenu.visible && (
                <div className="fixed inset-0 z-[1059]" onClick={(e) => { e.stopPropagation(); closeContextMenu(); }} />
            )}

            <div 
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] grid grid-rows-[auto_1fr]" 
                onClick={e => e.stopPropagation()}
            >
                <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Mapa Geral de Postes</h2>
                    <button 
                        onClick={onClose} 
                        className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                        <IconX className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                    </button>
                </header>
                
                <main ref={mapContainerRef} className="relative overflow-hidden rounded-b-2xl">
                    {isLoading ? (
                        <div className="absolute inset-0 flex justify-center items-center bg-white dark:bg-slate-900 z-10">
                            <Loader className="animate-spin h-12 w-12 text-blue-500" />
                        </div>
                    ) : filteredPoles.length === 0 ? (
                        <div className="flex justify-center items-center h-full">
                            <p className="text-slate-500 dark:text-slate-400">Nenhum poste com coordenadas encontrado.</p>
                        </div>
                    ) : (
                        <MapContainer
                            style={{ height: '100%', width: '100%' }}
                            className={isMapDark ? 'map-dark' : ''}
                            preferCanvas={true}
                            center={center}
                            zoom={zoom}
                            zoomSnap={0.1}
                            zoomDelta={0.1}
                        >
                            <MapEvents onContextMenu={handleContextMenu} />
                            <MapResizer />
                            <TileLayer url={tileUrl} attribution={tileAttribution} />

                            <StatusFilter selectedStatuses={selectedStatuses} onStatusChange={setSelectedStatuses} />
                            <LayersControl
                                layerVisibility={layerVisibility}
                                onToggleLayer={handleToggleLayer}
                                isLoading={isOzmapLoading}
                            />

                            {/* Renderização das Camadas Ozmap */}
                            {layerVisibility.ctos && ctosGeoJson && (
                                <GeoJSON
                                    key={`ctos-${ozmapData.ctos.length}`}
                                    data={ctosGeoJson}
                                    pointToLayer={ctoPointToLayer}
                                    onEachFeature={onEachFeature}
                                />
                            )}

                            {layerVisibility.ceos && ceosGeoJson && (
                                <GeoJSON
                                    key={`ceos-${ozmapData.ceos.length}`}
                                    data={ceosGeoJson}
                                    pointToLayer={ceoPointToLayer}
                                    onEachFeature={onEachFeature}
                                />
                            )}

                            {polesGeoJson && (
                                <GeoJSON
                                    key={`poles-${selectedStatuses.length}`}
                                    data={polesGeoJson}
                                    pointToLayer={pointToLayer}
                                    onEachFeature={onEachFeature}
                                />
                            )}

                            <MapLegend />
                            <MapFocusController poles={filteredPoles} />
                        </MapContainer>
                    )}
                </main>
            </div>
        </div>,
        document.body
    );
};

export default ProjectsMapModal;
